import { sendRequest } from "@infrastructure/api/api";
import { ActivityResponse } from "@features/admin/activity/types/activity";
import ApiResponse from "@core/types/apiResponse";
import { ApiMessageResponse } from "@core/types/apiMessageResponse";

/** Convert booleans to 1/0 and strip null/undefined before URLSearchParams */
const toQueryParams = (params: Record<string, any>): Record<string, string> => {
    const result: Record<string, string> = {};
    for (const [key, value] of Object.entries(params)) {
        if (value === undefined || value === null) continue;
        result[key] = typeof value === 'boolean' ? (value ? '1' : '0') : String(value);
    }
    return result;
};

export const fetchActivity = async (nationId: number, params: any): Promise<ApiResponse<ActivityResponse>> => {
    const response = await sendRequest<any, ActivityResponse>('GET', `nation/${nationId}/activity/query?${new URLSearchParams(toQueryParams(params))}`);
    return response;
};
export const fetchAssignedActivity = async (nationId: number, departmentId: number, templateId: number, params: any): Promise<ApiResponse<ActivityResponse>> => {
    const response = await sendRequest<any, ActivityResponse>('GET', `nation/${nationId}/department/${departmentId}/reporttemplate/${templateId}/activity/query?${new URLSearchParams(toQueryParams(params))}`);
    return response;
};

/**
 * Create a new activity for a nation.
 *
 * Sends a POST request to `nation/{nationId}/activity/create` with the provided payload.
 *
 * @param nationId - The numeric identifier of the nation to which the activity belongs.
 * @param data - The request payload containing activity details (should conform to the API's expected activity creation schema).
 * @returns A promise that resolves to an ApiResponse wrapping the created ActivityResponse.
 * @throws Rejects if the underlying HTTP request fails or the API responds with an error.
 */
export const createActivity = async (nationId: number, data: any): Promise<ApiResponse<ApiMessageResponse>> => {
    const response = await sendRequest<any, ApiMessageResponse>('POST', `nation/${nationId}/activity/create`, data);
    return response;
};

/**
 * Update an activity for a given nation.
 *
 * Sends a POST request to the backend endpoint `nation/{nationId}/activity/{activityId}/update`
 * with the provided payload to modify the activity.
 *
 * @param nationId - The numeric identifier of the nation that owns the activity.
 * @param activityId - The numeric identifier of the activity to update.
 * @param data - The request payload containing fields to update on the activity.
 * @returns A Promise that resolves to an ApiResponse containing the updated ActivityResponse.
 * @throws Will reject if the underlying request fails (network error or non-success HTTP response).
 */
export const updateActivity = async (nationId: number, activityId: number, data: any): Promise<ApiResponse<ApiMessageResponse>> => {
    const response = await sendRequest<any, ApiMessageResponse>('POST', `nation/${nationId}/activity/${activityId}/update`, data);
    return response;
};

/**
 * Assigns a KPI to a specific activity for a nation.
 *
 * Sends a POST request to `nation/{nationId}/activity/{activityId}/kpi/assign` with the provided payload.
 *
 * @param nationId - The nation identifier under which the activity exists.
 * @param activityId - The identifier of the activity to assign the KPI to.
 * @param data - Payload containing KPI assignment details (shape defined by the backend API).
 * @returns A Promise that resolves to an ApiResponse containing the ActivityResponse returned by the server.
 * @throws Will propagate errors thrown by sendRequest (e.g., network or API errors).
 */
export const assignActivityKpi = async (nationId: number, activityId: number, data: any): Promise<ApiResponse<ApiMessageResponse>> => {
    const response = await sendRequest<any, ApiMessageResponse>('POST', `nation/${nationId}/activity/${activityId}/kpi/assign`, data);
    return response;
};

export const removeActivityKpi = async (
    nationId: number,
    activityId: number,
    data: { kpi_ids: number[] }
): Promise<ApiResponse<ApiMessageResponse>> => {
    const response = await sendRequest<{ kpi_ids: number[] }, ApiMessageResponse>(
        'POST',
        `nation/${nationId}/activity/${activityId}/kpi/remove`,
        data
    );
    return response;
};

/** Disables an activity (sets it inactive). */
export const disableActivity = async (nationId: number, activityId: number): Promise<ApiResponse<ApiMessageResponse>> => {
    return sendRequest<never, ApiMessageResponse>('POST', `nation/${nationId}/activity/${activityId}/disable`);
};

/** Enables an activity (sets it active). */
export const enableActivity = async (nationId: number, activityId: number): Promise<ApiResponse<ApiMessageResponse>> => {
    return sendRequest<never, ApiMessageResponse>('POST', `nation/${nationId}/activity/${activityId}/enable`);
};
